import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Sun, Moon, ArrowUpRight, Loader2 } from "lucide-react";
import { login, setToken, verifyOtp, resendOtp } from "../api";
import { COMPANIES, resolveLogo } from "../companies";

const GROUP_TAGLINE = "One signing platform for every company under QGroup.";

const LogoSlide = ({ company, active }) => {
  const [err, setErr] = useState(false);
  const src = resolveLogo(company);
  useEffect(() => {
    setErr(false);
  }, [src]);
  return (
    <div
      className={`absolute inset-0 flex flex-col items-center justify-center px-10 text-center transition-opacity duration-[1100ms] ease-out ${
        active ? "opacity-100" : "opacity-0"
      }`}
    >
      <span className="flex h-44 w-44 items-center justify-center overflow-hidden rounded-[36px] bg-white shadow-2xl">
        {err ? (
          <span className="text-6xl font-extrabold text-[#1A1815]">
            {company.name.charAt(0)}
          </span>
        ) : (
          <img
            src={src}
            alt={company.name}
            onError={() => setErr(true)}
            className="h-full w-full object-contain p-7"
          />
        )}
      </span>
      <p className="mt-8 text-3xl font-bold tracking-tight text-white">
        {company.name}
      </p>
      <p className="mt-2.5 max-w-[340px] text-base font-medium leading-relaxed text-white/60">
        {GROUP_TAGLINE}
      </p>
    </div>
  );
};

const Login = ({ dark, toggleTheme, onLogin }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [slide, setSlide] = useState(0);
  // OTP (SMS 2FA) step
  const [otp, setOtp] = useState(null); // { pending_token, phone_hint } | null
  const [code, setCode] = useState("");

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % COMPANIES.length), 4000);
    return () => clearInterval(t);
  }, []);

  const active = COMPANIES[slide];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError("Please enter your email and password.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const data = await login(email.trim(), password);
      if (data.otp_required) {
        setOtp({ pending_token: data.pending_token, email_hint: data.email_hint });
        setCode("");
      } else {
        setToken(data.token);
        onLogin(data.user);
        navigate("/", { replace: true });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.trim().length < 4) {
      setError("Enter the code we emailed you.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const data = await verifyOtp(otp.pending_token, code.trim());
      setToken(data.token);
      onLogin(data.user);
      navigate("/", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Verification failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError("");
    try {
      await resendOtp(otp.pending_token);
    } catch {
      setError("Could not resend the code. Try again.");
    }
  };

  return (
    <div className="grid h-screen w-full overflow-hidden bg-[#FDFBFA] dark:bg-[#0B0810] md:grid-cols-2">
      {/* Left: form */}
      <div className="relative flex items-center justify-center px-6 py-10 sm:px-10">
        <button
          data-testid="login-theme-toggle"
          onClick={toggleTheme}
          title="Toggle theme"
          className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center rounded-full bg-[#F0EBE5] text-[#1A1815] transition-colors hover:bg-[#E7E0D8] active:scale-90 dark:bg-white/5 dark:text-white/70 dark:hover:bg-white/10"
        >
          {dark ? <Sun className="h-4 w-4" strokeWidth={1.8} /> : <Moon className="h-4 w-4" strokeWidth={1.8} />}
        </button>

        <div className="flex w-full max-w-[400px] flex-col items-center text-center">
          <span className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#1A1815] text-2xl font-extrabold tracking-tight text-white dark:bg-white dark:text-black">
            Qs<span style={{ color: "var(--accent)" }}>.</span>
          </span>

          {otp ? (
            <>
              <h1 className="text-4xl font-bold tracking-tight text-[#1A1815] dark:text-white">
                Verify it's you
              </h1>
              <p className="mt-2.5 text-base text-[#8B8680] dark:text-white/50">
                We emailed a 6-digit code to{" "}
                <span className="font-semibold text-[#1A1815] dark:text-white">{otp.email_hint}</span>.
                Enter it below to finish signing in.
              </p>
              <form onSubmit={handleVerify} className="mt-8 w-full space-y-3.5 text-left">
                <input
                  data-testid="otp-code-input"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={10}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="Enter code"
                  autoFocus
                  className="w-full rounded-2xl border border-[#E7E0D8] bg-white px-5 py-4 text-center text-lg font-bold tracking-[0.4em] text-[#1A1815] outline-none transition-colors placeholder:tracking-normal placeholder:text-[#B0A99F] focus:border-[#1A1815] dark:border-white/10 dark:bg-white/[0.04] dark:text-white dark:placeholder:text-white/35 dark:focus:border-white/40"
                />
                {error && (
                  <p data-testid="login-error" className="text-sm font-medium text-[#B42318] dark:text-[#FF8A80]">
                    {error}
                  </p>
                )}
                <button
                  data-testid="otp-verify-btn"
                  type="submit"
                  disabled={loading}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#1A1815] py-4 text-sm font-semibold text-white transition-transform hover:scale-[1.01] active:scale-[0.98] disabled:opacity-60 dark:bg-white dark:text-black"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & sign in"}
                </button>
                <div className="flex items-center justify-between pt-1 text-sm">
                  <button
                    type="button"
                    data-testid="otp-back-btn"
                    onClick={() => {
                      setOtp(null);
                      setError("");
                    }}
                    className="font-medium text-[#8B8680] hover:text-[#1A1815] dark:text-white/50 dark:hover:text-white"
                  >
                    ← Back
                  </button>
                  <button
                    type="button"
                    data-testid="otp-resend-btn"
                    onClick={handleResend}
                    className="font-semibold text-[#1A1815] hover:underline dark:text-white"
                  >
                    Resend code
                  </button>
                </div>
              </form>
            </>
          ) : (
            <>
              <h1 className="text-4xl font-bold tracking-tight text-[#1A1815] dark:text-white">
                Welcome back!
              </h1>
              <p className="mt-2.5 text-base text-[#8B8680] dark:text-white/50">
                Sign in to your QGroup workspace to continue.
              </p>

              <form onSubmit={handleSubmit} className="mt-8 w-full space-y-3.5 text-left">
                <input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address"
                  className="w-full rounded-2xl border border-[#E7E0D8] bg-white px-5 py-4 text-sm font-medium text-[#1A1815] outline-none transition-colors placeholder:text-[#B0A99F] focus:border-[#1A1815] dark:border-white/10 dark:bg-white/[0.04] dark:text-white dark:placeholder:text-white/35 dark:focus:border-white/40"
                />
                <div className="relative">
                  <input
                    data-testid="login-password-input"
                    type={showPass ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    className="w-full rounded-2xl border border-[#E7E0D8] bg-white px-5 py-4 pr-12 text-sm font-medium text-[#1A1815] outline-none transition-colors placeholder:text-[#B0A99F] focus:border-[#1A1815] dark:border-white/10 dark:bg-white/[0.04] dark:text-white dark:placeholder:text-white/35 dark:focus:border-white/40"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass((s) => !s)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8B8680] transition-colors hover:text-[#1A1815] dark:text-white/40 dark:hover:text-white/80"
                  >
                    {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>

                {error && (
                  <p data-testid="login-error" className="text-sm font-medium text-[#B42318] dark:text-[#FF8A80]">
                    {error}
                  </p>
                )}

                <button
                  data-testid="login-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#1A1815] py-4 text-sm font-semibold text-white transition-transform hover:scale-[1.01] active:scale-[0.98] disabled:opacity-60 dark:bg-white dark:text-black"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
                </button>
              </form>
            </>
          )}

          <p className="mt-7 text-xs text-[#8B8680] dark:text-white/40">
            Access is provisioned by QGroup IT. Contact your administrator for an account.
          </p>
        </div>
      </div>

      {/* Right: full-bleed branded company carousel */}
      <div className="hidden p-3 md:block">
        <div
          data-testid="login-brand-panel"
          className="relative h-full w-full overflow-hidden rounded-[28px]"
          style={{
            background: `linear-gradient(155deg, color-mix(in srgb, ${active.accent} 82%, #0B0810) 0%, #0B0810 68%)`,
            transition: "background 1100ms ease-out",
          }}
        >
          <div
            className="pointer-events-none absolute -left-24 -top-24 h-96 w-96 rounded-full blur-3xl"
            style={{
              background: `color-mix(in srgb, ${active.accent} 45%, transparent)`,
              transition: "background 1100ms ease-out",
            }}
          />
          <div className="pointer-events-none absolute -bottom-28 -right-20 h-96 w-96 rounded-full bg-white/5 blur-3xl" />

          <div className="absolute left-7 top-7 z-10">
            <a
              href="https://qgroup.gm"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-white backdrop-blur"
              style={{ background: "color-mix(in srgb, var(--accent) 55%, transparent)" }}
            >
              qgroup.gm <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.4} />
            </a>
          </div>

          {COMPANIES.map((c, i) => (
            <LogoSlide key={c.key} company={c} active={i === slide} />
          ))}

          <div className="absolute inset-x-0 bottom-9 z-10 flex justify-center gap-2">
            {COMPANIES.map((_, i) => (
              <button
                key={i}
                onClick={() => setSlide(i)}
                aria-label={`Company ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  i === slide ? "w-8 bg-white" : "w-3 bg-white/40"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
